import { type FC, useMemo } from 'react'

import isNil from 'lodash/isNil'

import { Dialog } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'

import { useProgress } from './use-progress'

interface Props {
  isOpen: boolean
  onClose: () => void
  listId: string
  listName: string
}

export const ListCreationProgressDialog: FC<Props> = ({
  isOpen,
  onClose,
  listId,
  listName,
}) => {
  const { progress, completed, success, failure, total } = useProgress(
    listId,
    isOpen,
  )

  const content = useMemo(() => {
    if (completed) {
      return (
        <h3 className="mt-3 text-center text-title">
          The list <span className="font-semibold">{listName}</span> has been
          created and <span className="font-semibold">{success} fans</span> have
          successfully been added to it.
        </h3>
      )
    }

    return (
      <>
        <div className="mb-4 mt-6 flex justify-between text-center text-sm text-title">
          {!isNil(success) && !isNil(failure) && !isNil(total) && (
            <span>
              {success + failure}/{total}
            </span>
          )}
          <span>{progress}%</span>
        </div>

        <Progress value={progress} />
      </>
    )
  }, [progress, completed, success, failure, total, listName])

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <Dialog.Header>
        <Dialog.Title>List creation progress</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description>{content}</Dialog.Description>
      <Dialog.ClosableFooter
        onClose={onClose}
        className="block"
        buttonClassName="w-full"
        closeText={completed ? 'Close' : 'Run in background'}
      />
    </Dialog>
  )
}
